import { IconCheck } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import Select, { StylesConfig, components, MultiValueProps } from "react-select";
import useIndexOtaChannel from "../../../../services/ota-channel/hooks/useIndexOtaChannel";

interface OptionType {
    value: string;
    label: string;
}

interface OtaChannelFilterComponentProps {
    onChange: (agentFilter: string[]) => void;
    defaultValue: string | undefined;
}

const OtaChannelSelectFilterComponent: React.FC<OtaChannelFilterComponentProps> = ({ onChange, defaultValue }) => {
    const defaultOta = { value: "0", label: "All Ota Channel" };
    const [otaChannels, setOtaChannels] = useState<OptionType[]>([defaultOta]);
    const [selectedOtaChannel, setSelectedOtaChannel] = useState<OptionType[]>([defaultOta]);

    useEffect(() => {
        if (defaultValue === '') {
            console.log('default value is empty');
            setSelectedOtaChannel([defaultOta]);
        }
    }, [defaultValue]);

    const { data: otaChannelData, isSuccess, isError, isFetching } = useIndexOtaChannel(
        {
            all: true
        }
    );

    useEffect(() => {
        if (otaChannelData) {
            setOtaChannels([
                defaultOta,
                ...otaChannelData.data.map((ota) => ({
                    value: ota.id,
                    label: ota.name,
                })),
            ]);
        }
    }, [otaChannelData]);

    useEffect(() => {
        if (otaChannelData && selectedOtaChannel.length === 1 && selectedOtaChannel[0].value === defaultOta.value) {
            onChange([]);
        }
    }, [otaChannelData, selectedOtaChannel]);

    return (
        <OtaChannelSelect
            defaultOptions={defaultOta}
            options={otaChannels}
            isFetching={isFetching}
            isSuccess={isSuccess}
            isError={isError}
            selectedOption={selectedOtaChannel}
            setSelectedOption={setSelectedOtaChannel}
            setFilter={onChange}
        />
    );
};

export default OtaChannelSelectFilterComponent;

const customStyles: StylesConfig<OptionType, boolean> = {
    menuPortal: (base) => ({
        ...base,
        zIndex: 1000, // Pastikan z-index cukup tinggi
    }),
};

interface OtaChannelSelectProps {
    defaultOptions: OptionType;
    options: OptionType[];
    isFetching: boolean;
    isSuccess: boolean;
    isError: boolean;
    selectedOption: OptionType[];
    setSelectedOption: (option: OptionType[]) => void;
    setFilter?: (filter: string[]) => void;
}

const CustomOption = (props: any) => {
    const { data, isSelected, innerRef, innerProps } = props;

    return (
        <div ref={innerRef} {...innerProps} className="d-flex justify-content-between align-items-center p-2">
            <span>{data.label}</span>
            {isSelected && <IconCheck size={16} />}
        </div>
    );
};

const OtaChannelSelect: React.FC<OtaChannelSelectProps> = ({
    defaultOptions,
    options,
    isFetching,
    isSuccess,
    isError,
    selectedOption,
    setSelectedOption,
    setFilter,
}) => {
    const handleChange = (selected: OptionType[]) => {
        if (selected.length === 1 && selected[0].value === defaultOptions.value) {
            setSelectedOption([defaultOptions]);
            setFilter?.([]);
        } else {
            setSelectedOption(selected.filter((option) => option.value !== defaultOptions.value));
            setFilter?.(selected.filter((option) => option.value !== defaultOptions.value).map((option) => option.value));
        }
    };

    return (
        <Select
            value={selectedOption}
            options={options}
            className="p-0"
            styles={customStyles}
            menuPortalTarget={document.body}
            menuPosition="absolute"
            isLoading={isFetching}
            isDisabled={!isSuccess || isError}
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            onChange={(selected) => handleChange(selected as OptionType[])}
            components={{
                Option: CustomOption,
                MultiValueContainer: ({ children, ...props }) => {
                    const selectedValues = props.selectProps.value as OptionType[];
                    const index = selectedValues.findIndex((v) => v.value === props.data.value);
                    const totalSelected = selectedValues.length;
                    const maxVisible = 3; // Maksimal item yang ditampilkan sebelum "X More"

                    if (index < maxVisible) {
                        return children;
                    }

                    if (index === maxVisible) {
                        const remainingCount = totalSelected - maxVisible;
                        return (
                            <div className="d-flex align-items-center px-2 bg-light rounded">
                                <span>+{remainingCount} More</span>
                            </div>
                        );
                    }

                    return null;
                },
                MultiValue: (props: MultiValueProps<any>) => {
                    const selectedValues = props.selectProps.value as OptionType[];
                    const index = selectedValues.findIndex((v) => v.value === props.data.value);
                    const maxVisible = 3;

                    if (index > maxVisible) return null;

                    return <components.MultiValue {...props} />;
                },
                ValueContainer: ({ getValue }) => {
                    const selected = getValue();
                    return (
                        <div className="p-2">
                            {selected.length === 1 && selected[0].value === defaultOptions.value
                                ? defaultOptions.label
                                : `${selected.length} Ota Channel Selected`}
                        </div>
                    );
                },
            }}
        />
    );
};